/**
 * product-card-dropdown.js — OMI Well Beauty
 *
 * ─────────────────────────────────────────────────────────────────
 * ACCESSIBILITY CHANGES — WCAG 2.2 AA  (Slade v. OMI remediation)
 * Section 7: Buttons on Collection/Shop Page
 * ─────────────────────────────────────────────────────────────────
 *
 * FIX A  Trigger selector updated from .product-form-inline_dropdown__content
 *        (a plain <div>) to [data-dropdown-trigger] (the new <button>).
 *
 * FIX B  aria-expanded synced on open/close/closeOther.
 *        WCAG 4.1.2 Name, Role, Value.
 *
 * FIX C  aria-selected synced on each option selection.
 *        WCAG 4.1.2.
 *
 * FIX D  Roving tabindex on options.
 *        WCAG 2.1.1 Keyboard.
 *
 * FIX E  Focus management on open.
 *        WCAG 2.4.3 Focus Order.
 *
 * FIX F  Focus management on close.
 *        WCAG 2.4.3 Focus Order.
 *
 * FIX G  Escape key closes the dropdown and returns focus to trigger.
 *        WCAG 2.1.2 No Keyboard Trap.
 *
 * FIX H  Arrow key navigation inside the listbox.
 *        WCAG 2.1.1 Keyboard.
 *
 * FIX I  Submit button aria-label kept in sync with price changes.
 *        Now also appends variant title/duration so AT reads:
 *        "Subscribe $69 for Hair Growth Peptides Gummies — 1-Month Delivery"
 *        "Subscribe $177 for … — 3-Month Delivery, originally $207"
 *        "Buy now $79 for … — One-Time Purchase"
 *        WCAG 1.3.1 / 2.4.6.
 *
 * FIX J  Trigger button aria-label updated on every option selection so
 *        AT reflects the current selection without opening the listbox:
 *        "Subscription options for [Product]. Currently selected:
 *         1-Month Delivery at $69 per month. Press to change."
 *        Initialised from the default-selected variant on page load.
 *        WCAG 4.1.2 Name, Role, Value.
 *
 * Everything else is IDENTICAL to the previous version.
 */

class ProductCardDropdown {
  constructor(productCard) {
    this.productCard = productCard;

    this.dropdown          = productCard.querySelector('.product-form__input--dropdown');
    this.triggerButton     = this.dropdown ? this.dropdown.querySelector('[data-dropdown-trigger]') : null;
    this.optionsContainer  = this.dropdown ? this.dropdown.querySelector('.product-form-inline_dropdown__options') : null;
    this.selectedContainer = this.dropdown ? this.dropdown.querySelector('[data-dropdown-selected]') : null;
    this.inlineForm        = productCard.querySelector('.product-form-inline-form');
    this.productTabs       = this.dropdown ? this.dropdown.querySelectorAll('.product-tab') : [];

    this.priceWrapper      = productCard.querySelector('.price-info-wpr');
    this.priceContainer    = this.priceWrapper ? this.priceWrapper.querySelector('.price__container') : null;
    if (!this.priceContainer) {
      this.priceContainer = productCard.querySelector('.price__container');
    }
    this.salePriceContainer    = this.priceContainer ? this.priceContainer.querySelector('.price__sale')    : null;
    this.regularPriceContainer = this.priceContainer ? this.priceContainer.querySelector('.price__regular') : null;

    const otpTabs = this.productCard.querySelectorAll('.one-time-purchase.product-tab');
    this.productTabs = [...this.productTabs, ...otpTabs];

    this.productName = (
      productCard.querySelector('.card__heading a')?.textContent?.trim() ||
      productCard.querySelector('[aria-label]')?.getAttribute('aria-label') ||
      ''
    );

    // FIX J: tracks the currently selected variant title so both the
    // trigger aria-label and the CTA aria-label stay in sync.
    this.currentVariantTitle = '';

    if (this.dropdown && this.triggerButton && this.optionsContainer) {
      this.init();
      this.initializeDefaultPrice();
    }

    this.handleResize = this.handleResize.bind(this);
    window.addEventListener('resize', this.handleResize);
  }

  init() {
    this.triggerButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeOtherDropdowns();
      const isOpen = this.dropdown.classList.contains('is-open');
      isOpen ? this.closeDropdown() : this.openDropdown();
    });

    this.optionsContainer.addEventListener('keydown', (e) => {
      const options      = Array.from(this.productTabs);
      const currentIndex = options.findIndex((tab) => tab === document.activeElement);

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          e.stopPropagation();
          this.closeDropdown();
          break;
        case 'ArrowDown':
          e.preventDefault();
          this.focusOption(options[currentIndex < options.length - 1 ? currentIndex + 1 : 0]);
          break;
        case 'ArrowUp':
          e.preventDefault();
          this.focusOption(options[currentIndex > 0 ? currentIndex - 1 : options.length - 1]);
          break;
        case 'Home':
          e.preventDefault();
          this.focusOption(options[0]);
          break;
        case 'End':
          e.preventDefault();
          this.focusOption(options[options.length - 1]);
          break;
        case 'Enter':
        case ' ':
          if (document.activeElement?.closest('.product-form-inline_dropdown__options')) {
            e.preventDefault();
            document.activeElement.click();
          }
          break;
      }
    });

    this.productTabs.forEach((tab) => {
      tab.addEventListener('click', (e) => {
        const isOtpTab  = tab.classList.contains('one-time-purchase');
        const hasButton = tab.querySelector('button, a');
        if (!isOtpTab || !hasButton) {
          e.preventDefault();
          e.stopPropagation();
        }

        // FIX C + FIX D: aria-selected and roving tabindex
        this.productTabs.forEach((t) => {
          t.classList.remove('selected');
          t.setAttribute('aria-selected', 'false');
          t.setAttribute('tabindex', '-1');
        });
        tab.classList.add('selected');
        tab.setAttribute('aria-selected', 'true');
        tab.setAttribute('tabindex', '0');

        const options = JSON.parse(tab.dataset.options || '{}');

        // FIX J: read variant_title directly from data-options — the
        // Shopify variant.title (e.g. "1-Month Delivery"). DOM scraping
        // was unreliable when merchants set custom h4 titles like
        // "Subscribe and Save" via metafields.
        this.currentVariantTitle = options?.variant_title || '';
        this.updateTriggerAriaLabel(options);

        // Update the dropdown display
        if (this.selectedContainer) {
          const contentWpr = tab.querySelector('.content-wpr');
          if (contentWpr) {
            this.selectedContainer.innerHTML = contentWpr.outerHTML;
          } else {
            const otpButton = tab.querySelector('.one-time-purchase--button');
            const otpLink   = tab.querySelector('.klaviyo-bis-trigger');
            if (otpButton) {
              this.selectedContainer.innerHTML = `<div class="content-wpr">${otpButton.outerHTML}</div>`;
            } else if (otpLink) {
              this.selectedContainer.innerHTML = `<div class="content-wpr">${otpLink.outerHTML}</div>`;
            }
          }
        }

        this.updateFormInputs(options);
        this.updateProductCardPrice(options);

        // FIX F: return focus to trigger
        this.closeDropdown();
      });
    });
  }

  // ── FIX J: keep the trigger button's aria-label current ───────
  // Called on tab click and on init. Reads price_per_month and
  // is_subscription directly from the data-options JSON so there is
  // no manual recalculation. Format:
  //   Subscription: "Subscription options for [Product]. Currently
  //                  selected: 1-Month Delivery at $69 per month.
  //                  Press to change."
  //   OTP:          "Purchase options for [Product]. Currently
  //                  selected: One-Time Purchase at $79.
  //                  Press to change."
  updateTriggerAriaLabel(options) {
    if (!this.triggerButton) return;

    const isSubscription = options?.is_subscription === true || options?.is_subscription === 'true';
    const displayPrice   = options?.price_per_month || options?.total_price || '';
    const title          = this.currentVariantTitle;

    const label = isSubscription
      ? `Subscription options for ${this.productName}. Currently selected: ${title} at ${displayPrice} per month. Press to change.`
      : `Purchase options for ${this.productName}. Currently selected: ${title} at ${displayPrice}. Press to change.`;

    this.triggerButton.setAttribute('aria-label', label);
  }

  // ── FIX I + FIX J: CTA aria-label ────────────────────────────
  // Format: "Subscribe 1-Month Delivery $69"
  //         "Subscribe 3-Month Delivery $177, originally $207"
  //         "Buy now One-Time Purchase $79"
  //         "Sold out"
  updateSubmitAriaLabel(labelText, options) {
    if (!this.inlineForm) return;
    const submitButton = this.inlineForm.querySelector('.product-form-inline-cart-submit');
    if (!submitButton) return;

    let label;

    if (options && options.available === false) {
      label = 'Sold out';
    } else if (labelText) {
      // "Add to Bag" is used as the action verb in the aria-label only.
      // Visible button text (Subscribe / Buy now) is set separately in
      // updateFormInputs() and is not affected here. WCAG 1.3.1 / 2.4.6.
      const actionVerb = 'Add to Bag';
      const totalPrice = options?.total_price || '';

      const compareAt  = this._parsePrice(options?.compare_at_price || options?.price_strikethrough);
      const current    = this._parsePrice(options?.price || options?.price_per_month || options?.total_price);
      const hasDiscount = compareAt && current && compareAt > current;

      // "Subscribe 1-Month Delivery $69"  or  "Buy now One-Time Purchase $79"
      label = `${actionVerb} ${this.currentVariantTitle} ${totalPrice}`;
      if (hasDiscount) label += `, originally ${this.formatPrice(compareAt)}`;
    } else {
      return;
    }

    submitButton.setAttribute('aria-label', label);
  }

  _parsePrice(value) {
    if (!value) return null;
    if (typeof value === 'number') return value > 100 ? value / 100 : value;
    const m = String(value).match(/[\d.]+/);
    if (!m) return null;
    const n = parseFloat(m[0]);
    return n > 100 ? n / 100 : n;
  }

  openDropdown() {
    this.dropdown.classList.add('is-open');
    this.optionsContainer.style.display = 'block';
    this.triggerButton.setAttribute('aria-expanded', 'true');
    this.adjustDropdownPosition();

    requestAnimationFrame(() => {
      const selectedOption = this.optionsContainer.querySelector('[aria-selected="true"]');
      (selectedOption || this.productTabs[0])?.focus();
    });
  }

  closeDropdown() {
    this.dropdown.classList.remove('is-open');
    this.optionsContainer.style.display = 'none';
    this.dropdown.classList.remove('dropdown-up');
    this.triggerButton.setAttribute('aria-expanded', 'false');

    if (this.optionsContainer.contains(document.activeElement)) {
      this.triggerButton.focus();
    }
  }

  focusOption(optionEl) {
    this.productTabs.forEach((t) => t.setAttribute('tabindex', '-1'));
    optionEl.setAttribute('tabindex', '0');
    optionEl.focus();
  }

  closeOtherDropdowns() {
    document.querySelectorAll('.product-form__input--dropdown.is-open').forEach((dropdown) => {
      if (dropdown !== this.dropdown) {
        dropdown.classList.remove('is-open');
        const opts = dropdown.querySelector('.product-form-inline_dropdown__options');
        if (opts) opts.style.display = 'none';
        const otherTrigger = dropdown.querySelector('[data-dropdown-trigger]');
        if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
      }
    });
  }

updateFormInputs(options) {
    if (!this.inlineForm || !options.product_id) return;

    const variantInput   = this.inlineForm.querySelector('input[name="id"]');
    let sellingPlanInput = this.inlineForm.querySelector('input[name="selling_plan"]');
    const quantityInput  = this.inlineForm.querySelector('input[name="quantity"]');

    if (variantInput) variantInput.value = options.product_id;

    if (options.selling_plan_id && options.selling_plan_id !== 'null' && options.selling_plan_id !== '') {
      if (!sellingPlanInput) {
        sellingPlanInput = document.createElement('input');
        sellingPlanInput.type = 'hidden';
        sellingPlanInput.name = 'selling_plan';
        if (variantInput?.nextSibling) {
          variantInput.parentNode.insertBefore(sellingPlanInput, variantInput.nextSibling);
        } else if (variantInput) {
          variantInput.parentNode.appendChild(sellingPlanInput);
        }
      }
      sellingPlanInput.value = options.selling_plan_id;
    } else {
      sellingPlanInput?.remove();
    }

    if (quantityInput) quantityInput.value = '1';

    const submitButton = this.inlineForm.querySelector('.product-form-inline-cart-submit');
    if (submitButton) {
      const buttonText = submitButton.querySelector('span');
      if (buttonText && options.available !== false) {
        let formattedPrice = options.total_price;
        if (formattedPrice) {
          formattedPrice = formattedPrice.replace(/\.00$|\.01$/, '');
          // ── CTA TEXT FIX ──────────────────────────────────────────────
          // Subscription variants → "Subscribe $XX"
          // One-time purchase variants → "Add to bag $XX"
          const isSubscription = options.is_subscription === true || options.is_subscription === 'true';
          const newLabel = isSubscription ? `Subscribe ${formattedPrice}` : `Add to bag ${formattedPrice}`;
          // ─────────────────────────────────────────────────────────────
          buttonText.textContent = newLabel;
          this.updateSubmitAriaLabel(newLabel, options);
        } else {
          const fallbackLabel = submitButton.dataset.addToCartText || 'Add to bag';
          buttonText.textContent = fallbackLabel;
          this.updateSubmitAriaLabel(fallbackLabel, options);
        }
        submitButton.disabled = false;
      } else if (buttonText && options.available === false) {
        buttonText.textContent = submitButton.dataset.soldOutText || 'Sold out';
        this.updateSubmitAriaLabel(null, options);
        submitButton.disabled = true;
      }
    }
  }

  initializeDefaultPrice() {
    const selectedTab = this.productTabs.find((tab) => tab.classList.contains('selected'))
      || this.productTabs[0];

    if (!selectedTab) return;

    const options = JSON.parse(selectedTab.dataset.options || '{}');

    // FIX J: seed currentVariantTitle from data-options.variant_title —
    // the raw Shopify variant title, not the merchant's custom h4 text.
    this.currentVariantTitle = options?.variant_title || '';

    this.updateProductCardPrice(options);
    this.updateTriggerAriaLabel(options); // FIX J: initialise trigger label

    if (this.inlineForm) {
      const submitButton = this.inlineForm.querySelector('.product-form-inline-cart-submit');
      const buttonText   = submitButton?.querySelector('span')?.textContent?.trim();
      if (buttonText) this.updateSubmitAriaLabel(buttonText, options); // FIX I + J
    }
  }

  updateProductCardPrice(options) {
    if (!this.priceContainer) return;

    let currentPrice = null;
    if (options.price)                        currentPrice = parseFloat(options.price);
    else if (options.price_per_month)         currentPrice = parseFloat(options.price_per_month);
    else if (options.variant_price)           currentPrice = parseFloat(options.variant_price);
    else if (options.total_price) {
      const m = String(options.total_price).match(/[\d.]+/);
      if (m) currentPrice = parseFloat(m[0]);
    }
    if (!currentPrice) return;
    if (currentPrice > 100) currentPrice /= 100;

    let compareAtPrice = null;
    const rawCompare = options.compare_at_price_per_month || options.compare_at_price || options.price_strikethrough;
    if (rawCompare) {
      const m = String(rawCompare).match(/[\d.]+/);
      if (m) compareAtPrice = parseFloat(m[0]);
    }
    if (compareAtPrice && compareAtPrice > 100) compareAtPrice /= 100;

    const formattedPrice   = this.formatPrice(currentPrice);
    const formattedCompare = compareAtPrice ? this.formatPrice(compareAtPrice) : null;
    const isOnSale         = compareAtPrice && compareAtPrice > currentPrice;

    if (isOnSale) {
      if (this.regularPriceContainer) this.regularPriceContainer.style.display = 'none';
      if (this.salePriceContainer) {
        this.salePriceContainer.style.display = 'block';
        const compareSpan = this.salePriceContainer.querySelector('s .price-item--regular, .price-item--regular');
        if (compareSpan) compareSpan.textContent = formattedCompare;
        const saleSpan = this.salePriceContainer.querySelector('.price-item--sale');
        if (saleSpan) saleSpan.textContent = formattedPrice;
      }
    } else {
      if (this.salePriceContainer) this.salePriceContainer.style.display = 'none';
      if (this.regularPriceContainer) {
        this.regularPriceContainer.style.display = 'block';
        const regularSpan = this.regularPriceContainer.querySelector('.price-item--regular');
        if (regularSpan) regularSpan.textContent = formattedPrice;
      } else {
        const anyPrice = this.priceContainer.querySelector('.price-item, .money, [data-price]');
        if (anyPrice) anyPrice.textContent = formattedPrice;
      }
    }
  }

  formatPrice(priceInDollars) {
    return '$' + priceInDollars.toFixed(2).replace(/\.00$/, '');
  }

  adjustDropdownPosition() {
    this.dropdown.classList.toggle('dropdown-up', this.isInLastRow());
  }

  isInLastRow() {
    const grid = this.productCard.closest('.product-grid');
    if (!grid) return false;
    const allCards   = Array.from(grid.querySelectorAll('[data-product-card]'));
    const idx        = allCards.indexOf(this.productCard);
    if (idx === -1) return false;
    const colCount   = window.getComputedStyle(grid).gridTemplateColumns.split(' ').length;
    const totalRows  = Math.ceil(allCards.length / colCount);
    const currentRow = Math.ceil((idx + 1) / colCount);
    return currentRow === totalRows;
  }

  handleResize() {
    if (this.dropdown?.classList.contains('is-open')) this.adjustDropdownPosition();
  }

  destroy() {
    window.removeEventListener('resize', this.handleResize);
  }
}

class ProductCardDropdownManager {
  constructor() {
    this.instances = new Map();
    this.init();
  }

  init() {
    this.initializeDropdowns();

    document.addEventListener('click', (e) => {
      if (!e.target.closest('.product-form__input--dropdown')) {
        this.closeAllDropdowns();
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.closeAllDropdowns();
    });

    this.observeNewCards();
  }

  initializeDropdowns() {
    document.querySelectorAll('[data-product-card]').forEach((card) => {
      const id = card.dataset.productCard;
      if (!this.instances.has(id)) this.instances.set(id, new ProductCardDropdown(card));
    });
  }

  closeAllDropdowns() {
    this.instances.forEach((instance) => {
      if (instance.dropdown?.classList.contains('is-open')) instance.closeDropdown();
    });
  }

  observeNewCards() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          const cards = node.hasAttribute?.('data-product-card')
            ? [node]
            : [...(node.querySelectorAll?.('[data-product-card]') || [])];
          cards.forEach((card) => {
            const id = card.dataset.productCard;
            if (!this.instances.has(id)) this.instances.set(id, new ProductCardDropdown(card));
          });
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.productCardDropdownManager = new ProductCardDropdownManager();
});